import { useImagesFastCache } from "@design-lab/entities/artwork";
import {
  BACKGROUND_TYPE,
  COLORS,
  PATTERNS,
  type BackgroundType
} from "@design-lab/shared/constants";
import { useEffect, useMemo } from "react";
import { Image, Rect } from "react-konva";
import type { ShirtData, Surface } from "../model/types";

type ShirtSurfaceProps = {
  surface: Surface;
  canvasWidth: number;
  canvasHeight: number;
  onLoad?: (shirtData: ShirtData) => void;
  backgroundType: BackgroundType;
};

// Simple white placeholder image (1x1) as base64
const PLACEHOLDER_IMAGE_BASE64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/58BAAMC/wGhOTzJAAAAAElFTkSuQmCC";

// Create placeholder image once outside component
const createPlaceholderImage = () => {
  const img = new window.Image();
  img.src = PLACEHOLDER_IMAGE_BASE64;
  return img;
};

const placeholderImage = createPlaceholderImage();

// Create the checkered pattern image synchronously
const createCheckeredPatternImage = (): HTMLImageElement => {
  const img = new window.Image();
  img.src = PATTERNS.CHECKERED_SVG;
  return img;
};

// Create the pattern image once outside the component
const checkeredPatternImage = createCheckeredPatternImage();

export function ShirtSurface({
  surface,
  canvasWidth,
  canvasHeight,
  onLoad,
  backgroundType
}: ShirtSurfaceProps) {
  const [loadedImage] = useImagesFastCache([surface.picture]);
  const shirtImage = loadedImage?.image || placeholderImage;

  // Calculate shirt positioning data
  const shirtData = useMemo(() => {
    if (!shirtImage || !canvasWidth || !canvasHeight) {
      return {
        scale: 1,
        offsetX: 0,
        offsetY: 0,
        width: 0,
        height: 0
      };
    }

    const imgWidth = shirtImage.width;
    const imgHeight = shirtImage.height;

    // Calculate scale to fit shirt within canvas
    const scale = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);
    const offsetX = (canvasWidth - imgWidth * scale) / 2;
    const offsetY = (canvasHeight - imgHeight * scale) / 2;

    return {
      scale,
      offsetX,
      offsetY,
      width: imgWidth,
      height: imgHeight
    };
  }, [shirtImage, canvasWidth, canvasHeight]);

  // Call onLoad when shirt data changes and actual image is loaded
  useEffect(() => {
    if (shirtImage && shirtImage !== placeholderImage && onLoad) {
      onLoad(shirtData);
    }
  }, [shirtData, shirtImage, onLoad]);

  // Render different backgrounds based on backgroundType
  if (backgroundType === BACKGROUND_TYPE.GREEN) {
    return (
      <Rect
        x={0}
        y={0}
        width={canvasWidth}
        height={canvasHeight}
        fill={COLORS.GREEN_BACKGROUND}
        listening={false}
        name="shirt-image"
      />
    );
  }

  if (backgroundType === BACKGROUND_TYPE.CHECKERED) {
    return (
      <Rect
        x={0}
        y={0}
        width={canvasWidth}
        height={canvasHeight}
        fillPatternImage={checkeredPatternImage}
        fillPatternRepeat="repeat"
        listening={false}
        name="shirt-image"
      />
    );
  }

  // Default: render shirt
  return (
    <Image
      image={shirtImage}
      x={shirtData.offsetX}
      y={shirtData.offsetY}
      scaleX={shirtData.scale}
      scaleY={shirtData.scale}
      listening={false} // Non-interactive (equivalent to selectable: false, evented: false)
      name="shirt-image"
    />
  );
}
